import React, { useEffect, useState } from 'react';
import SponserDetails from './SponserDetails';
import GameLogo from './GameLogo';
import TournamentLogo from './TournamentLogo';
import {
  bannerBgImgStyle,
  checkboxContainerStyle,
  checkboxDescriptionStyle,
  checkboxStyle,
  containerOverlayStyle,
  ctaDisabledStyle,
  ctaStyle,
  gameCtaContainerStyle,
  gameInstructionTextStyle,
  gameLandingContainerStyle,
  headerContainerStyle,
  headerGameLogoStyle,
  headerTournamentLogoStyle,
  howToPlayLinkStyle,
  linkStyle,
  mainContainerStyle,
  sponserWrapperStyle,
  titleStyle
} from './GameLandingScreenStyles';
import { useAppStore } from '../../hooks/useAppStore';
import {
  parseGameMapperId,
  triggerPlayQuizCTAEvent
} from '../../utils/watchAndWinUtils';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { GamificationScreens } from '../../store/interface';
import { DEVICE_TYPES } from '../../utils/constants/AppConstants';
import { setLocalStorage, setSessionStorage } from '../player/src/utils/Utils';
import {
  PLAY_GAMIFICATION_CTA_CLICKED,
  REDIRECT_URL_TO_CONSUMPTION
} from '../../utils/constants/localStorageConstants';
import { globalUrlHelper } from '../../utils/common';
import {
  HamburgerMenuLinks,
  HeaderLinks
} from '../../utils/constants/RouterConstants';
import { DEVICE_WIDTH_CONSTANTS } from '../../utils/constants/utilConstants';

const UnselectedCheckboxSvg = () => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      css={checkboxStyle}>
      <path
        d="M20 4V20H4V4H20ZM20 3H4C3.45 3 3 3.45 3 4V20C3 20.55 3.45 21 4 21H20C20.55 21 21 20.55 21 20V4C21 3.45 20.55 3 20 3Z"
        fill="white"
      />
    </svg>
  );
};
const SelectedCheckboxSvg = () => {
  return (
    <svg
      css={checkboxStyle}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20 3H4C3.45 3 3 3.45 3 4V20C3 20.55 3.45 21 4 21H20C20.55 21 21 20.55 21 20V4C21 3.45 20.55 3 20 3ZM17.94 8.82L10.96 15.8C10.81 15.96 10.61 16.03 10.4 16.03C10.19 16.03 10 15.96 9.85 15.8L6.06 12.01C5.87 11.82 5.87 11.5 6.06 11.31C6.26 11.11 6.57 11.11 6.77 11.31L10.4 14.94L17.23 8.12C17.43 7.92 17.74 7.92 17.94 8.12C18.13 8.31 18.13 8.63 17.94 8.82Z"
        fill="white"
      />
    </svg>
  );
};
const QuestionMarkIconSvg = () => {
  return (
    <svg
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.49967 1.33398C4.81967 1.33398 1.83301 4.32065 1.83301 8.00065C1.83301 11.6807 4.81967 14.6673 8.49967 14.6673C12.1797 14.6673 15.1663 11.6807 15.1663 8.00065C15.1663 4.32065 12.1797 1.33398 8.49967 1.33398ZM8.49967 14.0007C5.19301 14.0007 2.49967 11.3073 2.49967 8.00065C2.49967 4.69398 5.19301 2.00065 8.49967 2.00065C11.8063 2.00065 14.4997 4.69398 14.4997 8.00065C14.4997 11.3073 11.8063 14.0007 8.49967 14.0007ZM8.49967 12.0007C8.86786 12.0007 9.16634 11.7022 9.16634 11.334C9.16634 10.9658 8.86786 10.6673 8.49967 10.6673C8.13148 10.6673 7.83301 10.9658 7.83301 11.334C7.83301 11.7022 8.13148 12.0007 8.49967 12.0007ZM5.94641 6.02736C6.16641 4.76736 7.29975 3.88736 8.53308 4.00736C9.59975 4.11402 10.4931 5.00736 10.5997 6.08069C10.7197 7.22736 9.95308 8.28069 8.82641 8.59402V9.66069C8.82641 9.84736 8.67308 9.99402 8.49308 9.99402C8.31308 9.99402 8.15975 9.84736 8.15975 9.66069V8.32736C8.15975 8.17402 8.27308 8.04069 8.41308 8.00736C8.42641 8.00069 8.43975 8.00069 8.45308 8.00069C9.36641 7.84736 10.0264 7.01402 9.93975 6.14736C9.85975 5.38069 9.22641 4.74736 8.45975 4.66736C7.57975 4.57402 6.76641 5.22736 6.60641 6.14069C6.57308 6.32736 6.39975 6.44736 6.21975 6.41402C6.03975 6.38736 5.91975 6.21402 5.94641 6.02736Z"
        fill="white"
      />
    </svg>
  );
};

interface GameLandingScreenProps {
  isUserOnBoarded: boolean;
}

const GameLandingScreen: React.FC<GameLandingScreenProps> = ({
  isUserOnBoarded
}) => {
  const [isTermsAndConditionsAgreed, setTermsAndConditionsAgreed] =
    useState<boolean>(false);
  const {
    gamificationState = {},
    appConfig,
    setGamificationState,
    translations,
    isUserLoggedIn
  } = useAppStore();
  const { config = null } = gamificationState;
  const searchParams = useSearchParams();
  const gameMapperId = parseGameMapperId(
    searchParams.get('game_mapper_id') || ''
  );

  const onClickPlayQuizHandler = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setLocalStorage(PLAY_GAMIFICATION_CTA_CLICKED, 'true');
    triggerPlayQuizCTAEvent(gameMapperId as string);
    if (!isUserLoggedIn) {
      setSessionStorage(
        REDIRECT_URL_TO_CONSUMPTION,
        window.location.pathname + window.location.search
      );
      window.location.href = globalUrlHelper(HeaderLinks.SIGN_IN);
    } else {
      setGamificationState({ currentScreen: GamificationScreens.Quiz });
    }
  };

  const toggleTermsAndConditionsHandler = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setTermsAndConditionsAgreed(!isTermsAndConditionsAgreed);
  };

  const renderCheckbox = () => {
    if (isUserOnBoarded) {
      return null;
    }

    return (
      <div onClick={toggleTermsAndConditionsHandler}>
        {isTermsAndConditionsAgreed ? (
          <SelectedCheckboxSvg />
        ) : (
          <UnselectedCheckboxSvg />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (isUserOnBoarded) {
      setTermsAndConditionsAgreed(true);
    }
  }, [isUserOnBoarded]);

  const backgroundImage =
    appConfig.deviceType === DEVICE_TYPES.MOBILE &&
    (appConfig?.deviceWidth as number) <= DEVICE_WIDTH_CONSTANTS.deviceWidth600
      ? config?.onboardingBackgroundImage
      : config?.onboardingBackgroundImageDesktop;

  return (
    <section css={gameLandingContainerStyle}>
      {backgroundImage && (
        <img
          css={bannerBgImgStyle}
          src={backgroundImage}
          alt={
            translations?.gamification
              ?.WATCH_AND_WIN_LANDING_BACKGROUND_IMAGE_ALT
          }
        />
      )}
      <div css={containerOverlayStyle}>
        <div css={headerContainerStyle}>
          {appConfig.deviceType === DEVICE_TYPES.MOBILE &&
            config?.quizTitleImage && (
              <GameLogo extendedStyles={headerGameLogoStyle} />
            )}
          {config?.bannerLogoImage && (
            <TournamentLogo extendedStyles={headerTournamentLogoStyle} />
          )}
        </div>

        <div css={mainContainerStyle}>
          {config?.sponsorLogo && (
            <SponserDetails extendedStyles={sponserWrapperStyle} />
          )}
          {config?.quizPageTitle && (
            <h1 css={titleStyle}>{config?.quizPageTitle}</h1>
          )}
          {config?.quizInstructions?.[0] && (
            <p css={gameInstructionTextStyle}>
              {config?.quizInstructions?.[0]}
            </p>
          )}

          <div css={checkboxContainerStyle}>
            {renderCheckbox()}
            <p css={checkboxDescriptionStyle}>
              {!isUserOnBoarded &&
                `${translations?.gamification?.TERMS_AND_CONDITIONS_PREFIX_TEXT} `}
              <Link
                css={linkStyle}
                href={globalUrlHelper(
                  `${HamburgerMenuLinks.GAMIFICATION_ROUTE}/${HamburgerMenuLinks.TERMS}`
                )}
                target="_blank"
                aria-describedby={
                  translations?.gamification?.TERMS_AND_CONDITIONS_TEXT
                }>
                {translations?.gamification?.TERMS_AND_CONDITIONS_TEXT}
              </Link>{' '}
              &{' '}
              <Link
                css={linkStyle}
                href={globalUrlHelper(
                  `${HamburgerMenuLinks.GAMIFICATION_ROUTE}/${HamburgerMenuLinks.POLICY}`
                )}
                target="_blank"
                aria-describedby={
                  translations?.gamification?.PRIVACY_POLICY_TEXT
                }>
                {translations?.gamification?.PRIVACY_POLICY_TEXT}
              </Link>
            </p>
          </div>

          <div css={gameCtaContainerStyle}>
            {config?.cta2Text && (
              <a
                css={howToPlayLinkStyle}
                href={globalUrlHelper(
                  `${HamburgerMenuLinks.GAMIFICATION_ROUTE}/${HamburgerMenuLinks.HOWTOPLAY}`
                )}
                target="_blank">
                <QuestionMarkIconSvg />
                <span>{config?.cta2Text}</span>
              </a>
            )}
            {config?.ctaText && (
              <button
                onClick={onClickPlayQuizHandler}
                css={[
                  ctaStyle,
                  !isTermsAndConditionsAgreed && ctaDisabledStyle
                ]}>
                <img
                  src={`/webapp-assets/images/ILT/cricket-ball-icon.svg?v=${process.env.APP_VERSION}`}
                  alt="Cricket Ball"
                />
                {config?.ctaText}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameLandingScreen;
